import React, { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  Camera,
  Upload,
  Check,
  X,
  Image as ImageIcon,
  FileText,
  ArrowLeft,
  AlertCircle,
} from "lucide-react";
import insurancePolicyService from "../lib/InsurancePolicyService";
import { useAuth } from "../hooks/useAuth";
import ConfirmModal from "./ConfirmModal";
import {
  useInteractionTracking,
  useErrorTracking,
} from "../hooks/useAnalytics";
import DocumentPreviewModal from "./DocumentPreviewModal";
import analyticsService from "../lib/AnalyticsService";
import { useExtractionStream } from "../hooks/useExtractionStream";

// Upload stages configuration — runs on timers in parallel with the actual API call
const UPLOAD_STAGES = [
  { id: "setting-up", label: "Setting up", duration: 2000, image: "/upload/SETTING-UP.gif" },
  { id: "uploading", label: "Uploading document", duration: 6000, image: "/upload/UPLOADING.gif" },
  { id: "parsing", label: "Parsing document", duration: 3500, image: "/upload/PARSING.gif" },
  { id: "extracting", label: "Extracting data with AI", duration: 4000, image: "/upload/EXTRACTING.gif" },
  { id: "organizing", label: "Organizing your info", duration: 1500, image: "/upload/ORGANIZING.gif" },
];

const DocumentUpload = ({
  userData,
  onComplete,
  onBack,
  onFullApp,
  reupload,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [documentType, setDocumentType] = useState("DEC_Auto");
  const [showSampleModal, setShowSampleModal] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const { user, getIdToken, loading: authLoading, logout } = useAuth(); // Get the authenticated user and loading state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [checkingExisting, setCheckingExisting] = useState(true);
  const [existingPolicy, setExistingPolicy] = useState(null);
  const { trackButtonClick } = useInteractionTracking();
  const [openPreviewModal, setOpenPreviewModal] = useState(false);

  // Upload stage state (null = not uploading, or stage id)
  const [uploadStage, setUploadStage] = useState(null);
  const uploadResultRef = useRef(null);

  // SSE extraction stream hook
  const {
    startStream,
    abort: abortStream,
    stage: sseStage,
    message: sseMessage,
    extractedData,
    completed: sseCompleted,
    error: sseError,
  } = useExtractionStream();

  // Elapsed time counter — ticks every second while overlay is visible
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  useEffect(() => {
    if (uploadStage) {
      setElapsedSeconds(0);
      const interval = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [!!uploadStage]); // only reset when uploadStage goes null ↔ non-null

  // Drive uploadStage from SSE events
  useEffect(() => {
    if (sseStage) {
      setUploadStage(sseStage);
    }
  }, [sseStage]);

  // Summary card state
  const [showSummary, setShowSummary] = useState(false);
  const [summaryCountdown, setSummaryCountdown] = useState(5);

  // Handle SSE completion — show summary on success, skip straight to next step on error
  useEffect(() => {
    if (sseCompleted && uploadResultRef.current) {
      // Save extracted data to sessionStorage for downstream screens
      if (extractedData.quick || extractedData.full) {
        sessionStorage.setItem(
          "extractedData",
          JSON.stringify(extractedData)
        );
      }

      if (sseError) {
        // Extraction failed — skip summary, advance silently (non-blocking)
        handleSummaryAdvance();
      } else {
        setShowSummary(true);
      }
    }
  }, [sseCompleted, extractedData, sseError]);

  // Auto-advance countdown when summary is shown
  useEffect(() => {
    if (!showSummary) return;
    setSummaryCountdown(5);
    const interval = setInterval(() => {
      setSummaryCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          handleSummaryAdvance();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [showSummary]);

  const handleSummaryAdvance = () => {
    setShowSummary(false);
    setUploadStage(null);
    setUploadSuccess(true);
    setTimeout(() => {
      if (onComplete) {
        onComplete(uploadResultRef.current);
      }
    }, 500);
  };

  // Detect if the user is on a mobile device
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
      setIsMobile(mobileRegex.test(userAgent));
    };

    checkMobile();

    // Also check on resize in case of orientation changes
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Track page view on mount
  useEffect(() => {
    analyticsService.trackPageView("document_upload", {
      reupload: !!reupload,
    });
  }, [reupload]);

  // Check for existing policies on component mount
  useEffect(() => {
    const checkExistingPolicies = async () => {
      try {
        setCheckingExisting(true);
        const token = await getIdToken();

        // Use the same method as InsurancePolicyUploader
        const result = await insurancePolicyService.checkExistingPolicies(
          token
        );

        if (result) {
          setExistingPolicy(result.policy);

          // If policy exists, complete immediately
          if (onComplete) {
            onComplete(result.policy);
          }
        }
      } catch (err) {
        console.error("Error checking existing policies:", err);
        // Continue with upload flow even if check fails
      } finally {
        setCheckingExisting(false);
      }
    };
    console.log("DOCUMENTUPLOADSCREEN=" + reupload);
    if (reupload) {
      setCheckingExisting(false);
    } else {
      checkExistingPolicies();
    }
  }, []);

  const validateFile = (file) => {
    // Size limit: 10MB
    const MAX_SIZE = 10 * 1024 * 1024;

    // Allow images and PDFs
    if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
      throw new Error("Please upload an image or PDF file");
    }

    if (file.size > MAX_SIZE) {
      throw new Error("File size must be less than 10MB");
    }
  };

  const handleFileSelect = (file) => {
    try {
      validateFile(file);
      setSelectedFile(file);

      // Create preview
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        // For PDFs, just show an icon/name (no preview)
        setPreview(null);
      }

      setError(null);
      setUploadSuccess(false);
    } catch (err) {
      setError(err.message);
      setSelectedFile(null);
      setPreview(null);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files?.[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    try {
      if (!selectedFile) {
        throw new Error("Please select a file first");
      }

      setUploadStage("setting-up");
      setError(null);
      uploadResultRef.current = null;

      const token = await getIdToken();

      // Step 1: Upload file
      setUploadStage("uploading");
      const fileUrl = await insurancePolicyService.uploadFile(
        selectedFile,
        token
      );

      // Step 2: Submit checkin
      const checkinResult = await insurancePolicyService.submitPolicyCheckin(
        fileUrl,
        token
      );

      const result = {
        success: true,
        result: checkinResult,
        fileUrl,
      };
      uploadResultRef.current = result;

      console.log("Upload successful:", result);

      // Store submission info in session storage
      sessionStorage.setItem(
        "documentData",
        JSON.stringify({
          documentType,
          timestamp: new Date().toISOString(),
          fileName: selectedFile.name,
          policyCheckInId: checkinResult?.id || null,
        })
      );

      // Step 3: Start SSE extraction stream (drives stages from here)
      setUploadStage("parsing");
      startStream(checkinResult.id, token);

    } catch (err) {
      console.error("Upload error:", err);
      abortStream();
      setError(err.message || "An unexpected error occurred");
      setUploadStage(null);
    }
  };

  const handleTryCamera = () => {
    if (isMobile && cameraInputRef.current) {
      cameraInputRef.current.click();
    } else {
      // On desktop where camera might not be reliable, show a helpful message
      setError(
        "Camera works best on mobile devices. You may continue if your device has a camera, or use the gallery option instead."
      );
      setTimeout(() => {
        if (cameraInputRef.current) {
          cameraInputRef.current.click();
        }
      }, 1500);
    }
  };
  if (checkingExisting) {
    return (
      <div className="space-y-6 p-4">
        <div className="flex justify-center items-center p-8">
          <div className="animate-spin w-8 h-8 border-4 border-[#7B61FF] border-t-transparent rounded-full"></div>
          <span className="ml-3">Checking for existing documents...</span>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-6 p-4">
      {/* Translucent loading overlay — progress view OR summary view */}
      {(uploadStage || showSummary) && (() => {
        const currentStage = UPLOAD_STAGES.find((s) => s.id === uploadStage);
        const currentIndex = UPLOAD_STAGES.findIndex((s) => s.id === uploadStage);
        const quickData = extractedData?.quick;
        const fullData = extractedData?.full;
        const summaryData = fullData || quickData;
        const vehicles = fullData?.vehicles || [];
        const totalPremium = fullData?.total_premium_cents
          ? (parseInt(fullData.total_premium_cents, 10) / 100).toFixed(2)
          : null;
        const coverageCount = vehicles.reduce(
          (sum, v) => sum + (v.coverages?.length || 0), 0
        );

        return (
          <div className="fixed inset-0 flex flex-col items-center z-50 overflow-y-auto pt-12 pb-8">
            {/* GIF background */}
            {!showSummary && currentStage?.image && (
              <img
                src={currentStage.image}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
            {/* Dark overlay on top of GIF */}
            <div className="absolute inset-0 bg-black/25"></div>
            {/* Content on top */}
            <div className="relative z-10 flex flex-col items-center w-full">
            {showSummary ? (
              <>
                {/* Summary card */}
                <div className="w-[90%] max-w-md bg-white rounded-xl px-5 py-5 shadow-xl space-y-4">
                  {/* Header */}
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-[#A1CE90]" />
                    <p className="text-sm font-bold text-gray-800 uppercase tracking-wide">
                      Extraction Complete
                    </p>
                  </div>

                  {/* Policy basics */}
                  <div className="space-y-1">
                    {summaryData?.carrier_name && (
                      <p className="text-base font-semibold text-gray-900">
                        {summaryData.carrier_name}
                      </p>
                    )}
                    {summaryData?.carrier_policy_number && (
                      <p className="text-sm text-gray-600">
                        Policy #{summaryData.carrier_policy_number}
                      </p>
                    )}
                    {(summaryData?.effective_date || summaryData?.expiry_date) && (
                      <p className="text-sm text-gray-500">
                        {summaryData.effective_date}
                        {summaryData.expiry_date && ` → ${summaryData.expiry_date}`}
                      </p>
                    )}
                    {summaryData?.insured_name && (
                      <p className="text-sm text-gray-600">
                        {summaryData.insured_name}
                        {fullData?.insured?.name && fullData.insured.name !== summaryData.insured_name
                          ? ` (${fullData.insured.name})`
                          : ""}
                      </p>
                    )}
                  </div>

                  {/* Vehicle info — only if full extract has vehicles */}
                  {vehicles.length > 0 && (
                    <div className="border-t pt-3 space-y-2">
                      {vehicles.map((vehicle, i) => (
                        <div key={i}>
                          <p className="text-sm font-semibold text-gray-800">
                            {[vehicle.year, vehicle.make, vehicle.model]
                              .filter(Boolean)
                              .join(" ")}
                          </p>
                          {vehicle.vin && (
                            <p className="text-xs text-gray-500">
                              VIN: {vehicle.vin}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Coverage summary — only if available */}
                  {(coverageCount > 0 || totalPremium) && (
                    <div className="border-t pt-3 flex items-center justify-between">
                      {coverageCount > 0 && (
                        <p className="text-sm text-gray-600">
                          {coverageCount} coverage{coverageCount !== 1 ? "s" : ""} found
                        </p>
                      )}
                      {totalPremium && (
                        <p className="text-sm font-semibold text-gray-800">
                          Total Premium: ${totalPremium}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Continue button + countdown */}
                  <div className="flex items-center justify-between pt-2">
                    <button
                      onClick={handleSummaryAdvance}
                      className="uppercase font-bold bg-purpleGradient text-white px-6 py-2.5 rounded-[10px] hover:bg-purpleGradientDark transition-colors text-sm"
                    >
                      Continue
                    </button>
                    <p className="text-xs text-gray-400">
                      Continuing in {summaryCountdown}s...
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Progress view — status + quick extract + GIF + dots */}
                {/* Status card with spinner + elapsed time */}
                <div className="w-[90%] max-w-md mb-3 bg-white/90 backdrop-blur-md rounded-xl px-5 py-4 shadow-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 border-2 border-[#7B61FF] border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
                    <p className="text-sm font-semibold text-gray-700">
                      {sseMessage || currentStage?.label}
                    </p>
                  </div>
                  <p className="text-xs text-gray-400 mt-1 ml-8">
                    {elapsedSeconds}s elapsed
                  </p>
                </div>

                {/* Extracted data card — fades in after quick_extract */}
                <div
                  className={`w-[90%] max-w-md mb-4 transition-all duration-500 ${
                    quickData
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 -translate-y-2 h-0 mb-0 overflow-hidden"
                  }`}
                >
                  {quickData && (
                    <div className="bg-white/90 backdrop-blur-md rounded-xl px-5 py-4 shadow-lg space-y-2">
                      <p className="text-xs font-semibold text-[#7B61FF] uppercase tracking-wide mb-2">
                        Policy Found
                      </p>
                      {quickData.insured_name && (
                        <div className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-[#A1CE90] flex-shrink-0" />
                          <span className="text-sm text-gray-700">{quickData.insured_name}</span>
                        </div>
                      )}
                      {quickData.carrier_name && (
                        <div className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-[#A1CE90] flex-shrink-0" />
                          <span className="text-sm text-gray-700">{quickData.carrier_name}</span>
                        </div>
                      )}
                      {quickData.carrier_policy_number && (
                        <div className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-[#A1CE90] flex-shrink-0" />
                          <span className="text-sm text-gray-700">Policy #{quickData.carrier_policy_number}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Progress dots */}
                <div className="flex items-center gap-2 mt-4">
                  {UPLOAD_STAGES.map((stage, index) => (
                    <div
                      key={stage.id}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        index <= currentIndex
                          ? "bg-[#7B61FF] w-6"
                          : "bg-gray-300 w-2"
                      }`}
                    />
                  ))}
                </div>

                <p className="text-sm text-white/80 mt-3">
                  Please don't close this window
                </p>
              </>
            )}
            </div>
          </div>
        );
      })()}

      {/* Back button if onBack is provided */}
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to options
        </button>
      )}

      {/* Progress Overview */}
      <Card className="bg-[#CEC5FF]/25 border-[#CEC5FF]">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Check className="w-5 h-5 text-[#A1CE90] mt-1" />
            <div>
              <p className="text-sm text-[#1B1B1E]">
                Upload your insurance document
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Document Type Selection */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-2">
            <p className="text-sm text-[#1B1B1E]">
              Upload your Insurance Declaration Page{" "}
              <a
                onClick={() => setOpenPreviewModal(true)}
                className="underline text-[#1B1B1E] cursor-pointer"
              >
                (see sample)
              </a>
              {" "}or Insurance Card
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Upload Area */}
      <Card>
        <CardHeader>
          <CardTitle>Upload Document</CardTitle>
        </CardHeader>
        <CardContent>
          {!selectedFile ? (
            <div className="space-y-4">
              {/* File Input (hidden) */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileInput}
                accept="image/*,application/pdf"
                className="hidden"
              />
              {/* Camera Input (hidden) */}
              <input
                type="file"
                ref={cameraInputRef}
                onChange={handleFileInput}
                accept="image/*"
                capture="environment"
                className="hidden"
              />
              {/* Upload Options */}
              <div
                className={
                  isMobile ? "grid grid-cols-2 gap-4" : "flex justify-center"
                }
              >
                <button
                  onClick={() => {
                    trackButtonClick("file_upload", {
                      step_name: "document_upload",
                      user_id: userData?.id,
                    });
                    fileInputRef.current?.click();
                  }}
                  className="flex flex-col items-center gap-2 p-6 border-2 border-dashed rounded-lg hover:border-[#7B61FF] hover:bg-[#CEC5FF]/20 transition-colors"
                >
                  <ImageIcon className="w-8 h-8 text-[#7B61FF]" />
                  <span className="text-sm font-medium">Choose from Files</span>
                </button>

                {isMobile && (
                  <button
                    onClick={() => {
                      trackButtonClick("camera", {
                        step_name: "document_upload",
                        user_id: userData?.id,
                      });
                      handleTryCamera();
                    }}
                    className="flex flex-col items-center gap-2 p-6 border-2 border-dashed rounded-lg hover:border-[#7B61FF] hover:bg-[#CEC5FF]/20 transition-colors"
                  >
                    <Camera className="w-8 h-8 text-[#7B61FF]" />
                    <span className="text-sm font-medium">Take Photo</span>
                  </button>
                )}
              </div>
              {isMobile ? (
                <p className="text-sm text-gray-500 text-center mt-2">
                  You can take a photo or upload from your gallery
                </p>
              ) : (
                <p className="text-sm text-gray-500 text-center mt-2">
                  Upload an image or PDF document (max 10MB)
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Preview */}
              <div className="relative">
                {preview ? (
                  // Image preview
                  <img
                    src={preview}
                    alt="Selected file preview"
                    className="w-full rounded-lg"
                  />
                ) : (
                  // PDF preview (just show an icon and filename)
                  <div className="p-8 border rounded-lg bg-gray-50 flex flex-col items-center justify-center">
                    <FileText className="w-12 h-12 text-[#7B61FF] mb-2" />
                    <span className="font-medium text-gray-900 break-all">
                      {selectedFile.name}
                    </span>
                    <span className="text-sm text-gray-500 mt-1">
                      PDF Document
                    </span>
                  </div>
                )}
                <button
                  onClick={() => {
                    trackButtonClick("upload", {
                      step_name: "document_upload",
                      user_id: userData?.id,
                    });
                    setShowConfirmModal(true);
                  }}
                  className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-lg hover:bg-gray-100"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
                <ConfirmModal
                  open={showConfirmModal}
                  onOpenChange={setShowConfirmModal}
                  onConfirm={() => {
                    setSelectedFile(null);
                    setPreview(null);
                    setShowConfirmModal(false);
                  }}
                />
              </div>

              {/* Success message */}
              {uploadSuccess && (
                <div className="flex items-start gap-2 p-3 bg-green-50 text-green-800 rounded-lg text-sm">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p>Document uploaded successfully!</p>
                </div>
              )}

              {/* Upload Button */}
              <button
                onClick={handleUpload}
                disabled={uploadStage !== null}
                className={`w-full flex items-center justify-center gap-2 uppercase font-bold bg-purpleGradient !mt-10 text-white px-4 py-3 rounded-[10px] hover:bg-purpleGradientDark transition-colors ${
                  uploadStage !== null ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                {uploadStage !== null ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    <span>Upload Document</span>
                  </>
                )}
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 text-red-800 rounded-lg text-sm">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p>{error}</p>
        </div>
      )}

      {/* Full App Option */}
      {/* {onFullApp && (
        <button
          onClick={onFullApp}
          className="w-full text-center text-gray-600 hover:text-gray-900 text-sm"
        >
          Switch to full Ownli experience
        </button>
      )} */}
      <DocumentPreviewModal
        open={openPreviewModal}
        onOpenChange={setOpenPreviewModal}
      />
    </div>
  );
};

export default DocumentUpload;
